import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { Box, Button, Stack } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import type { PortalWidget } from "@entrilia/shared/api/portalWidgetsTypes";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import {
  anytime,
  lastMonth,
  lastQuarter,
  lastYear,
  next30Days,
  nextQuarter,
  nextSevenDays,
  thisMonth,
  thisQuarter,
  thisYear,
  today,
} from "@entrilia/shared/utilities/dateFilterHelper";
import type { CashFlowPaymentStatus } from "../../api/types/widgetTypes";
import { allCashFlowPaymentStatuses } from "../../api/types/widgetTypes";
import { cashFlowPaymentStatusCaptionMap } from "../../utilities/enumCaptions";
import WidgetCardWrapper from "./WidgetCardWrapper";
import WidgetContent from "./WidgetContent";
import WidgetHeader from "./WidgetHeader";
import { useCashFlowsWidgetData } from "./widgetHooks";

interface Props {
  widgetSettings: PortalWidget;
  withFixedHeight?: boolean;
}

const dateRanges: DescriptiveDateRange[] = [
  anytime,
  today,
  nextSevenDays,
  next30Days,
  nextQuarter,
  thisMonth,
  thisQuarter,
  thisYear,
  lastMonth,
  lastQuarter,
  lastYear,
];

const CashFlowsWidget = ({ widgetSettings, withFixedHeight }: Props) => {
  const gridApiRef = useGridApiRef();
  const {
    parameters,
    report,
    fetchError,
    isRunningReport,
    investorIds,
    setInvestorIds,
    fundIds,
    setFundIds,
    paymentStatuses,
    setPaymentStatuses,
    dateRange,
    setDateRange,
    searchValue,
    setSearchValue,
    filteredRows,
    clearFilters,
    isAnyFilterApplied,
  } = useCashFlowsWidgetData(widgetSettings);

  return (
    <WidgetCardWrapper withFixedHeight={withFixedHeight} withFullScreenButton>
      <WidgetHeader name={widgetSettings.name}>
        <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <MultiSelectFilter
              options={parameters?.investors ?? []}
              selectedValues={investorIds}
              onChange={setInvestorIds}
              getOptionValue={(inv) => inv.id}
              getOptionLabel={(inv) => inv.name}
              placeholder="Investors"
              StartIcon={PersonIcon}
              disabled={!parameters?.investors.length}
            />

            <MultiSelectFilter
              options={parameters?.funds ?? []}
              selectedValues={fundIds}
              onChange={setFundIds}
              getOptionValue={(fund) => fund.id}
              getOptionLabel={(fund) => fund.name}
              placeholder="Funds"
              StartIcon={CompaniesIcon}
              disabled={!parameters?.funds.length}
            />

            <MultiSelectFilter<CashFlowPaymentStatus>
              options={[...allCashFlowPaymentStatuses]}
              selectedValues={paymentStatuses}
              onChange={(statuses) => setPaymentStatuses(statuses as CashFlowPaymentStatus[])}
              getOptionValue={(status) => status}
              getOptionLabel={(status) => cashFlowPaymentStatusCaptionMap[status]}
              placeholder="Status"
              StartIcon={AccountBalanceWalletIcon}
              disabled={isRunningReport}
            />

            <DateRangeSelect
              dateRange={dateRange}
              onChange={setDateRange}
              predefinedRanges={dateRanges}
              placeholder="Due Date"
              disabled={isRunningReport}
            />

            {isAnyFilterApplied && (
              <Button variant="text" color="primary" disabled={isRunningReport} onClick={clearFilters}>
                Clear all
              </Button>
            )}
          </Stack>

          <Box sx={{ ml: "auto" }}>
            <SearchField
              initialValue={searchValue}
              onSearch={setSearchValue}
              disabled={!report?.rows.length || isRunningReport}
              debounceTimeMs={300}
            />
          </Box>
        </Stack>
      </WidgetHeader>
      <WidgetContent
        report={report}
        rows={filteredRows}
        footnote={widgetSettings.footnote}
        loading={isRunningReport}
        fetchError={fetchError}
        gridApiRef={gridApiRef}
      />
    </WidgetCardWrapper>
  );
};

export default CashFlowsWidget;
